import * as i0 from '@angular/core';
import { InjectionToken, inject, EventEmitter, Component, Input, Output, NgModule, Injectable, Inject, ErrorHandler, APP_INITIALIZER, ViewChild, ViewChildren } from '@angular/core';
import * as i1$2 from '@angular/common';
import { CommonModule, Location } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpErrorResponse, HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { map, switchMap, catchError as catchError$1 } from 'rxjs/operators';
import { from, Observable, map as map$1, of, tap, catchError, throwError, finalize, interval, startWith, mergeMap, EMPTY, take, firstValueFrom, lastValueFrom, BehaviorSubject, Subject } from 'rxjs';
import * as i1$1 from '@angular/forms';
import { FormControl, FormGroup } from '@angular/forms';
import * as CryptoJS from 'crypto-js';
import * as i3 from '@azure/msal-angular';
import { MsalService } from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';

/**
 * CONFIG is an InjectionToken used to inject a configuration object
 * adhering to the IConfig interface. It provides a mechanism to pass
 * configuration data to the parts of the application where Angular’s
 * dependency injection cannot reach, such as services.
 *
 * Example:
 * providers: [
 *   { provide: CONFIG, useValue: yourConfigObject }
 * ]
 */
const CONFIG = new InjectionToken('coffee config');

// [https://developers.google.com/identity/gsi/web/reference/html-reference#element_with_class_g_id_signin]
class CoffeeSocialGoogleButtonComponent {
    constructor(renderer, elementRef) {
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.config = inject(CONFIG);
        this.errorMessage = '';
        this.type = 'standard';
        this.theme = 'outline';
        this.size = 'large';
        this.text = 'signin_with';
        this.shape = 'rectangular';
        this.logoAlignment = 'left';
        this.width = 400;
        this.onResponse = new EventEmitter();
    }
    ngAfterViewInit() {
        if (!this.config?.auth?.google?.clientId) {
            this.errorMessage = 'Google Authentication Configuration Missing: Ensure that the "auth" ' +
                'configuration property is provided when calling "CoffeeModule.forRoot({})"' +
                ' and that it includes "google" authentication settings.';
            throw Error(this.errorMessage);
        }
        this.loadScript('https://accounts.google.com/gsi/client', () => {
            const element = this.elementRef.nativeElement.querySelector('#google-button');
            this.init(element);
        });
    }
    loadScript(scriptUrl, callback) {
        const script = this.renderer.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.defer = true;
        script.onload = callback;
        this.renderer.appendChild(this.elementRef.nativeElement, script);
    }
    init(element) {
        google.accounts.id.initialize({
            client_id: this.config.auth?.google?.clientId,
            callback: (response) => this.handleGoogleSignIn(response)
        });
        google.accounts.id.renderButton(element, {
            type: this.type,
            size: this.size,
            theme: this.theme,
            text: this.text,
            shape: this.shape,
            logoAlignment: this.logoAlignment,
            width: this.width
        });
    }
    handleGoogleSignIn(response) {
        let base64Url = response.credential.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        let userData = JSON.parse(jsonPayload);
        const model = {
            id: userData.sub,
            name: userData.name,
            email: userData.email,
            photoUrl: userData.picture
        };
        this.onResponse.emit(model);
    }
}
CoffeeSocialGoogleButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeSocialGoogleButtonComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
CoffeeSocialGoogleButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CoffeeSocialGoogleButtonComponent, selector: "ngx-coffee-social-google-button", inputs: { type: "type", theme: "theme", size: "size", text: "text", shape: "shape", logoAlignment: "logoAlignment", width: "width" }, outputs: { onResponse: "onResponse" }, ngImport: i0, template: "<div id=\"google-button\">{{ errorMessage }}</div>" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeSocialGoogleButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-coffee-social-google-button', template: "<div id=\"google-button\">{{ errorMessage }}</div>" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { type: [{
                type: Input
            }], theme: [{
                type: Input
            }], size: [{
                type: Input
            }], text: [{
                type: Input
            }], shape: [{
                type: Input
            }], logoAlignment: [{
                type: Input
            }], width: [{
                type: Input
            }], onResponse: [{
                type: Output
            }] } });

class CoffeeSocialGoogleButtonModule {
}
CoffeeSocialGoogleButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeSocialGoogleButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CoffeeSocialGoogleButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CoffeeSocialGoogleButtonModule, declarations: [CoffeeSocialGoogleButtonComponent], imports: [CommonModule], exports: [CoffeeSocialGoogleButtonComponent] });
CoffeeSocialGoogleButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeSocialGoogleButtonModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeSocialGoogleButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CoffeeSocialGoogleButtonComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        CoffeeSocialGoogleButtonComponent
                    ]
                }]
        }] });

const getPropertyName = (type) => {
    if (typeof type === 'string') {
        return type;
    }
    const parsedTypeValues = type.toString().split('.');
    let propertyName = '';
    if (parsedTypeValues.length > 0) {
        parsedTypeValues.shift();
        propertyName = parsedTypeValues.join('.');
    }
    return propertyName;
};
/**
 * @summary
 * Search values by the exact condition
 * @example
 * .where('name', '==', 'text')
 * .where((model) => model.name, '==', 'text')
 * .where((model) => model.age, '>', 18)
 */
const where = (type, operator, value) => {
    const propertyName = getPropertyName(type);
    return {
        expression: `${propertyName}${operator}${value}`,
        type: 'filter'
    };
};
/**
 * @summary
 * Search values by a boolean type
 * @example
 * .where('hasStatus', true)
 * .where((model) => model.isValid, false)
 */
const whereIs = (type, value) => {
    const propertyName = getPropertyName(type);
    return {
        expression: `${propertyName}==${value}`,
        type: 'filter'
    };
};
/**
 * @summary
 * Search values that contains the value, it will use '%like%' on database
 * @example
 * .whereContains('name', 'text')
 * .whereContains((model) => model.name, 'text')
 */
const whereContains = (type, value) => {
    const propertyName = getPropertyName(type);
    return {
        expression: `${propertyName}@=${value}`,
        type: 'filter'
    };
};
/**
 * @summary
 * Search value that is in array
 * @example
 * .whereIn('id', 'year', [1,2])
 * .whereIn((model) => model.id, [1,2])
 */
const whereIn = (type, values) => {
    const propertyName = getPropertyName(type);
    const operator = '@IN@';
    const value = values.join("||");
    return {
        expression: `${propertyName}${operator}${value}`,
        type: 'filter'
    };
};
/**
 * @summary
 * Search value that is not in array
 * @example
 * .whereNotIn('id', [1,2])
 * .whereNotIn((model) => model.id, [1,2])
 */
const whereNotIn = (type, values) => {
    const propertyName = getPropertyName(type);
    const operator = '@NOTIN@';
    const value = values.join("||");
    return {
        expression: `${propertyName}${operator}${value}`,
        type: 'filter'
    };
};
/**
 * @summary
 * Search value that is in array
 * @example
 * .whereOr([where('name', '==', 'bmx'), whereContains('name', 'a')],[whereDate('createdAt', 'DD-MM-YYYY', '27-10-2022')])
 *
 * result => (name == 'bmx' AND name LIKE '%a%') OR (createdAt == '27-10-2022')
 */
const whereOr = (...filters) => {
    const operator = '@or@';
    const expression = filters.map(filter => filter.map(value => value.expression.replace(',', "@:@")).join('@:@')).join(operator);
    return {
        expression: expression,
        type: 'filter'
    };
};
/**
 * @summary
 * Filter by date with various options for filtering by day, month, year, or combinations.
 *
 * @param type The property name or a lambda function to specify the property.
 * @param operator The comparison operator to use ('==', '!=', '<=', '<', '>=', '>').
 * @param date The date value to compare.
 * @param filterBy Specifies the components of the date to filter by ('day', 'month', 'year', 'day-month', 'day-year', 'month-year', 'day-month-year', 'full-date').
 *
 * @example
 * // Filter by day
 * whereDate('createdAt', '==', new Date(), 'day');
 * whereDate((model) => model.createdAt, '==', new Date(), 'day');
 *
 * @example
 * // Filter by month
 * whereDate('createdAt', '==', new Date(), 'month');
 * whereDate((model) => model.createdAt, '==', new Date(), 'month');
 *
 * @example
 * // Filter by year
 * whereDate('createdAt', '==', new Date(), 'year');
 * whereDate((model) => model.createdAt, '==', new Date(), 'year');
 *
 * @example
 * // Filter by day and month
 * whereDate('createdAt', '==', new Date(), 'day-month');
 * whereDate((model) => model.createdAt, '==', new Date(), 'day-month');
 *
 * @example
 * // Filter by day and year
 * whereDate('createdAt', '==', new Date(), 'day-year');
 * whereDate((model) => model.createdAt, '==', new Date(), 'day-year');
 *
 * @example
 * // Filter by month and year
 * whereDate('createdAt', '==', new Date(), 'month-year');
 * whereDate((model) => model.createdAt, '==', new Date(), 'month-year');
 *
 * @example
 * // Filter by day, month, and year
 * whereDate('createdAt', '==', new Date(), 'day-month-year');
 * whereDate((model) => model.createdAt, '==', new Date(), 'day-month-year');
 *
 * @example
 * // Filter by full date, (YYYY-MM-DD HH:mm:ss)
 * whereDate('createdAt', '==', new Date());
 * whereDate((model) => model.createdAt, '==', new Date());
 */
const whereDate = (type, operator, date, filterBy = 'full-date') => {
    const propertyName = getPropertyName(type);
    // Format the date components
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const fullDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    // Add operator prefix
    let operatorPrefix = '';
    switch (operator) {
        case '==':
            operatorPrefix = 'cEqualDate';
            break;
        case '!=':
            operatorPrefix = 'cNotEqualDate';
            break;
        case '<=':
            operatorPrefix = 'cLessEqualDate';
            break;
        case '<':
            operatorPrefix = 'cLessDate';
            break;
        case '>=':
            operatorPrefix = 'cGreaterEqualDate';
            break;
        case '>':
            operatorPrefix = 'cGreaterDate';
            break;
    }
    let expression = '';
    switch (filterBy) {
        case 'day':
            expression = `${operatorPrefix}${propertyName}=day=${day}`;
            break;
        case 'month':
            expression = `${operatorPrefix}${propertyName}=month=${month}`;
            break;
        case 'year':
            expression = `${operatorPrefix}${propertyName}=year=${year}`;
            break;
        case 'day-month':
            expression = `${operatorPrefix}${propertyName}=day=${day},${operatorPrefix}${propertyName}=month=${month}`;
            break;
        case 'day-year':
            expression = `${operatorPrefix}${propertyName}=day=${day},${operatorPrefix}${propertyName}=year=${year}`;
            break;
        case 'month-year':
            expression = `${operatorPrefix}${propertyName}=month=${month},${operatorPrefix}${propertyName}=year=${year}`;
            break;
        case 'day-month-year':
            expression = `${operatorPrefix}${propertyName}=day=${day},${operatorPrefix}${propertyName}=month=${month},${operatorPrefix}${propertyName}=year=${year}`;
            break;
        case 'full-date':
            expression = `${operatorPrefix}${propertyName}=full-date=${fullDate}`;
            break;
    }
    return {
        expression: expression,
        type: 'filter'
    };
};
/**
 * @summary
 * Sort by property
 * @example
 * .sortBy('createdAt', 'desc')
 * .sortBy((model) => model.createdAt, 'asc')
 */
const sortBy = (type, sort) => {
    const propertyName = getPropertyName(type);
    const sortSymbol = sort == 'asc' ? '' : '-';
    return {
        expression: `${sortSymbol}${propertyName}`,
        type: 'sort'
    };
};
/**
 * @summary
 * Sets query Parameter
 * @example
 * .withQueryParameter('name', 'text')
 * .withQueryParameter((model) => model.name, 'text')
 */
const withQueryParameter = (type, value) => {
    const propertyName = getPropertyName(type);
    return {
        expression: `${propertyName}=${value}`,
        type: 'parameter'
    };
};

class Pager {
    constructor(pager) {
        this.totalItems = 0;
        this.currentPage = 1;
        this.pageSize = 0;
        this.totalPages = 0;
        this.startPage = 0;
        this.endPage = 0;
        this.startIndex = 0;
        this.endIndex = 0;
        this.pages = [];
        if (pager) {
            this.totalItems = pager.totalItems;
            this.currentPage = pager.currentPage;
            this.pageSize = pager.pageSize;
            this.totalPages = pager.totalPages;
            this.startPage = pager.startPage;
            this.endPage = pager.endPage;
            this.startIndex = pager.startIndex;
            this.endIndex = pager.endIndex;
            this.pages = pager.pages;
        }
    }
}

class FilterResponse {
    constructor(data, pager) {
        if (data) {
            this.data = data;
        }
        else {
            this.data = [];
        }
        if (pager) {
            this.pager = new Pager(pager);
        }
        else {
            this.pager = new Pager();
        }
    }
}

class CoffeeRequestGet {
    constructor(httpClient, url, ...args) {
        this.httpClient = httpClient;
        this.queryParameters = [];
        this.authorizationToken = null;
        this.apiUrl = url;
        this.queryParameters = args;
    }
    /**
     * @summary
     * Search values by the exact condition
     * @example
     * .where('name', '==', 'text')
     * .where((model) => model.name, '==', 'text')
     * .where((model) => model.age, '>', 18)
     */
    where(type, operator, value) {
        this.queryParameters.push(where(type, operator, value));
        return this;
    }
    /**
   * @summary
   * Search values by a boolean type
   * @example
   * .where('hasStatus', true)
   * .where((model) => model.isValid, false)
   */
    whereIs(type, value) {
        this.queryParameters.push(whereIs(type, value));
        return this;
    }
    /**
     * @summary
     * Search values that contains the value, it will use '%like%' on database
     * @example
     * .whereContains('name', 'text')
     * .whereContains((model) => model.name, 'text')
     */
    whereContains(type, value) {
        this.queryParameters.push(whereContains(type, value));
        return this;
    }
    /**
    * @summary
    * Search value that is in array
    * @example
    * .whereIn('id', [1,2])
    * .whereIn((model) => model.id, [1,2])
    */
    whereIn(type, values) {
        this.queryParameters.push(whereIn(type, values));
        return this;
    }
    /**
    * @summary
    * Search matches
    * @example
    * .whereOr([where('name', '==', 'bmx'), whereContains('name', 'a')],[whereDate('createdAt', 'DD-MM-YYYY', '27-10-2022')])
    *
    * result => (name == 'bmx' AND name LIKE '%a%') OR (createdAt == '27-10-2022')
    */
    whereOr(...filters) {
        this.queryParameters.push(whereOr(...filters));
        return this;
    }
    /**
    * @summary
    * Search value that is not in array
    * @example
    * .whereNotIn('id', [1,2])
    * .whereNotIn((model) => model.id, [1,2])
    */
    whereNotIn(type, values) {
        this.queryParameters.push(whereNotIn(type, values));
        return this;
    }
    /**
     * @summary
     * Filter by date with various options for filtering by day, month, year, or combinations.
     *
     * @param type The property name or a lambda function to specify the property.
     * @param operator The comparison operator to use ('==', '!=', '<=', '<', '>=', '>').
     * @param date The date value to compare.
     * @param filterBy Specifies the components of the date to filter by ('day', 'month', 'year', 'day-month', 'day-year', 'month-year', 'day-month-year', 'full-date').
     *
     * @example
     * // Filter by day
     * whereDate('createdAt', '==', new Date(), 'day');
     * whereDate((model) => model.createdAt, '==', new Date(), 'day');
     *
     * @example
     * // Filter by month
     * whereDate('createdAt', '==', new Date(), 'month');
     * whereDate((model) => model.createdAt, '==', new Date(), 'month');
     *
     * @example
     * // Filter by year
     * whereDate('createdAt', '==', new Date(), 'year');
     * whereDate((model) => model.createdAt, '==', new Date(), 'year');
     *
     * @example
     * // Filter by day and month
     * whereDate('createdAt', '==', new Date(), 'day-month');
     * whereDate((model) => model.createdAt, '==', new Date(), 'day-month');
     *
     * @example
     * // Filter by day and year
     * whereDate('createdAt', '==', new Date(), 'day-year');
     * whereDate((model) => model.createdAt, '==', new Date(), 'day-year');
     *
     * @example
     * // Filter by month and year
     * whereDate('createdAt', '==', new Date(), 'month-year');
     * whereDate((model) => model.createdAt, '==', new Date(), 'month-year');
     *
     * @example
     * // Filter by day, month, and year
     * whereDate('createdAt', '==', new Date(), 'day-month-year');
     * whereDate((model) => model.createdAt, '==', new Date(), 'day-month-year');
     *
     * @example
     * // Filter by full date, (YYYY-MM-DD HH:mm:ss)
     * whereDate('createdAt', '==', new Date());
     * whereDate((model) => model.createdAt, '==', new Date());
     */
    whereDate(type, operator, date, filterBy = 'full-date') {
        this.queryParameters.push(whereDate(type, operator, date, filterBy));
        return this;
    }
    /**
    * @summary
    * Sort by property
    * @example
    * .sortBy('createdAt', 'desc')
    * .sortBy((model) => model.createdAt, 'asc')
    */
    sortBy(type, sort) {
        this.queryParameters.push(sortBy(type, sort));
        return this;
    }
    /**
     * Sets the authorization token to be used in HTTP requests.
     * @param token - The authorization token.
     */
    useAuthorizationToken(token) {
        this.authorizationToken = token;
        return this;
    }
    /**
    * @summary
    * Sets query Parameter
    * @example
    * .withQueryParameter('name', 'text')
    * .withQueryParameter((model) => model.name, 'text')
    */
    withQueryParameter(type, value) {
        this.queryParameters.push(withQueryParameter(type, value));
        return this;
    }
    /**
     * @summary
     * Returns unique single value
     * @example
     * .returnSingleValue()
     *
     * result => true || 1 || 'string'
     */
    returnSingleValue() {
        let url = '';
        url = this.parseUrl(url);
        const headers = this.getHeaders();
        return this.httpClient.get(url, { headers });
    }
    /**
     * @summary
     * Returns unique object of type
     * @example
     * .returnSingleObjectOf(Type)
     *
     * result => { }
     */
    returnSingleObjectOf(type) {
        let url = '';
        url = this.parseUrl(url);
        const headers = this.getHeaders();
        return this.httpClient.get(url, { headers }).pipe(map(data => new type(data)));
    }
    /**
     * @summary
     * Returns list of pagination type
     * @example
     * .returnPaginationListOf(Type, { currentPage: 1, pageSize: 10 })
     *
     * result => { data: [], pageer: {} }
     */
    returnPaginationListOf(type, pagination) {
        const url = this.parseUrl('', pagination);
        const headers = this.getHeaders();
        return this.httpClient.get(url, { headers }).pipe(map(data => {
            const values = data.data.map((entity) => {
                return new type(entity);
            });
            const pager = new Pager(data.pager);
            return new FilterResponse(values, pager);
        }));
    }
    /**
     * @summary
     * Returns list of type
     *
     * @example
     * .returnListOf(Type)
     *
     * result => []
     */
    returnListOf(type) {
        const url = this.parseUrl();
        const headers = this.getHeaders();
        return this.httpClient.get(url, { headers }).pipe(map((data) => {
            if (Array.isArray(data)) {
                return data.map((entity) => {
                    return new type(entity);
                });
            }
            else {
                return data.data.map((entity) => {
                    return new type(entity);
                });
            }
        }));
    }
    /**
     * Downloads a file from the server and saves it with the given file name.
     *
     * @param fileNameWithExtension - The desired file name, including the file extension (e.g., 'file.xlsx').
     * @returns An Observable that completes when the file has been downloaded.
     *
     * Usage example:
     * .download('file.xlsx').subscribe(() => {
     *   console.log('File downloaded successfully');
     * });
     */
    download(fileNameWithExtension) {
        let headers = this.getHeaders();
        headers = headers.set('Content-Type', 'application/octet-stream');
        const url = this.parseUrl();
        return this.httpClient
            .get(url, { headers, responseType: 'blob' })
            .pipe(map((data) => {
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileNameWithExtension;
            link.click();
            window.URL.revokeObjectURL(url);
            return data;
        }));
    }
    getHeaders() {
        let headers = new HttpHeaders();
        if (this.authorizationToken) {
            headers = headers.set('Authorization', this.authorizationToken);
        }
        return headers;
    }
    parseUrl(suffixEndpoint, pagination) {
        let params = [];
        let url = this.extractLastUrl(this.apiUrl);
        const filters = this.queryParameters.filter(param => param.type == 'filter');
        const sorts = this.queryParameters.filter(param => param.type == 'sort');
        const queryParameters = this.queryParameters.filter(param => param.type == 'parameter');
        if (filters.length) {
            params.push(`filters=${filters.map(filter => filter.expression).join(',')}`);
        }
        if (sorts.length) {
            params.push(`sorts=${sorts.map(sort => sort.expression).join(',')}`);
        }
        if (pagination && pagination.currentPage > -1) {
            params.push(`currentPage=${pagination.currentPage}`);
        }
        if (pagination && pagination.pageSize > -1) {
            params.push(`pageSize=${pagination.pageSize}`);
        }
        if (queryParameters.length) {
            params = params.concat(queryParameters.map(param => param.expression));
        }
        if (!suffixEndpoint?.startsWith("http")) {
            url += suffixEndpoint ?? '';
        }
        else {
            url = suffixEndpoint ?? '';
        }
        if (params.length > 0) {
            return url + '?' + params.join('&');
        }
        return url;
    }
    extractLastUrl(apiUrl) {
        const urls = apiUrl.split('http').filter(segment => segment.trim() !== '');
        if (urls.length > 0) {
            // Append 'http' back to the URL fragment unless it's already a complete URL segment
            return 'http' + urls[urls.length - 1];
        }
        return apiUrl; // Fallback to the original URL if no 'http' was found
    }
}

var CoffeeUtil;
(function (CoffeeUtil) {
    CoffeeUtil.concatUrl = (currentUrl, suffixUrl) => {
        if (!suffixUrl) {
            return currentUrl;
        }
        let parsedSuffixUrl = String(suffixUrl);
        if (!parsedSuffixUrl) {
            return currentUrl;
        }
        if (currentUrl.endsWith("/")) {
            currentUrl = currentUrl.substring(0, currentUrl.length - 1);
        }
        if (parsedSuffixUrl.startsWith("/")) {
            parsedSuffixUrl = parsedSuffixUrl.substring(1, parsedSuffixUrl.length);
        }
        return `${currentUrl}/${parsedSuffixUrl}`;
    };
    CoffeeUtil.convertModelToFormData = (val, formData = new FormData(), namespace = '') => {
        if (typeof val !== 'undefined' && val !== null) {
            if (val instanceof Date) {
                formData.append(namespace, val.toISOString());
            }
            else if (val instanceof Array) {
                for (let index = 0; index < val.length; index++) {
                    const element = val[index];
                    CoffeeUtil.convertModelToFormData(element, formData, namespace + '[' + index + ']');
                }
            }
            else if (typeof val === 'object' && !(val instanceof File)) {
                for (const propertyName in val) {
                    if (val.hasOwnProperty(propertyName)) {
                        CoffeeUtil.convertModelToFormData(val[propertyName], formData, namespace ? namespace + '.' + propertyName : propertyName);
                    }
                }
            }
            else if (val instanceof File) {
                formData.append(namespace, val);
            }
            else {
                formData.append(namespace, val.toString());
            }
        }
        return formData;
    };
})(CoffeeUtil || (CoffeeUtil = {}));

class CoffeeRequestPostPut {
    constructor(httpClient, encryptService, url, vo, usePut, isFormData = false, config) {
        this.httpClient = httpClient;
        this.encryptService = encryptService;
        this.isFormData = isFormData;
        this.config = config;
        this.usePut = false;
        this.encrypt = false;
        this.queryParameters = Array();
        this.authorizationToken = null;
        this.usePut = usePut;
        this.vo = vo;
        this.url = url;
        this.data = isFormData ? CoffeeUtil.convertModelToFormData(vo) : vo;
    }
    /**
   * Marks the request data to be encrypted before sending. This method will only
   * encrypt the data if `isFormData` is set to false, as encryption is not applied
   * to FormData objects. If `isFormData` is true, calling this method will not
   * affect the request data.
   *
   * This method respects the `disableEncryption` setting in the application configuration.
   * If `disableEncryption` is set to true in the configuration, encryption will be bypassed
   * regardless of the `isFormData` value.
   *
   * @returns {CoffeeRequestPostPut<T>} The current instance of the request with encryption settings applied.
   */
    withEncryption() {
        this.encrypt = this.config.disableEncryption ? false : true;
        return this;
    }
    /**
     * Appends a new segment to the current URL of the request.
     * This method allows for dynamic construction of endpoint URLs by concatenating
     * additional path segments. It's particularly useful when you need to build
     * nested or parameterized URLs programmatically.
     *
     * @param newUrlSegment The new URL segment to be appended to the current URL.
     *
     * Example usage:
     * .withUrlSegment('details').withUrlSegment('123') would transform the URL from "/base/path" to "/base/path/details/123"
     */
    withUrlSegment(newUrlSegment) {
        this.url = CoffeeUtil.concatUrl(this.url, newUrlSegment);
        return this;
    }
    /**
     * Adds a query parameter to the request's URL. If multiple query parameters are added,
     * they are concatenated with an '&' character. This method facilitates the dynamic addition
     * of query parameters to the request URL, allowing for complex queries and filtering options
     * to be specified programmatically.
     *
     * @param key The key of the query parameter to add.
     * @param value The value of the query parameter.
     * @returns {CoffeeRequestPostPut<T>} The instance of CoffeeRequestPostPut for method chaining.
     *
     * Example usage:
     * .withQueryParameter('page', '1').withQueryParameter('limit', '10')
     * would append "?page=1&limit=10" to the URL.
     */
    withQueryParameter(key, value) {
        this.queryParameters.push(`${key}=${value}`);
        return this;
    }
    /**
     * Converts the request's data to FormData. This conversion is suitable for scenarios requiring
     * file uploads or when the content type is multipart/form-data. Once converted, the data is
     * handled as FormData for the outgoing request.
     *
     * This method is particularly useful for dynamically preparing the payload for file uploads or
     * when interacting with APIs that expect multipart/form-data content types. It simplifies the
     * process of adjusting the payload format to meet specific endpoint requirements.
     *
     * Note: This conversion is one-way and designed for objects or data structures that can be
     * appropriately represented as FormData. Attempting to convert non-object data (e.g., a plain
     * string) may not yield the expected FormData structure and could require pre-processing or
     * manual adjustment of the data prior to conversion.
     */
    withFormData() {
        this.isFormData = true;
        this.data = CoffeeUtil.convertModelToFormData(this.vo);
        return this;
    }
    /**
     * Sets the authorization token to be used in HTTP requests.
     * @param token - The authorization token.
     */
    useAuthorizationToken(token) {
        this.authorizationToken = token;
        return this;
    }
    /**
     * Prepares and returns an Observable for the HTTP request, allowing further chaining with RxJS operators.
     *
     * @returns {Observable<T>} An Observable that emits the result of the HTTP request.
     */
    prepare() {
        const requestData = this.encrypt ? from(this.encryptDataBeforeSend()) : from([this.data]);
        let headers = this.getHeaders();
        return requestData.pipe(switchMap(data => {
            if (this.queryParameters.length) {
                this.url = `${this.url}?${this.queryParameters.join("&")}`;
            }
            if (this.encrypt && !(data instanceof FormData) && data != false) {
                headers = headers.set('encrypted', 'true');
            }
            else {
                data = this.data;
            }
            if (this.usePut) {
                return this.httpClient.put(this.url, data, { headers });
            }
            else {
                return this.httpClient.post(this.url, data, { headers });
            }
        }));
    }
    /**
     * Subscribes to the observable created by the HTTP request, initiating the request process.
     * The request is made using HTTP POST unless useHttpPutWhenId has been called and the data object has an 'id' property greater than 0.
     * If encryptData has been called, the data is encrypted before sending.
     *
     * @param observerOrNext A partial Observer or a next function to handle the emitted value.
     * @returns A subscription to the observable representing the request.
     */
    subscribe(observerOrNext) {
        return this.prepare().subscribe(observerOrNext);
    }
    /**
     * Encrypts the data before sending if encryption has been enabled.
     * If the data is FormData or encryption is not enabled, the original data is returned unchanged.
     *
     * @returns A promise resolving to the encrypted data, the original data, or false if encryption fails.
     */
    async encryptDataBeforeSend() {
        if (!this.encrypt || this.isFormData) {
            return this.data;
        }
        const encryptedData = await this.encryptService.encrypt(this.data);
        return encryptedData;
    }
    getHeaders() {
        let headers = new HttpHeaders();
        if (this.authorizationToken) {
            headers = headers.set('Authorization', this.authorizationToken);
        }
        return headers;
    }
}

class CoffeeRquestSave extends CoffeeRequestPostPut {
    constructor(httpClient, encryptService, url, vo, isFormData = false, config) {
        super(httpClient, encryptService, url, vo, false, isFormData, config);
    }
    /**
    * Configures the request to use HTTP PUT method when the data object has an 'id' property greater than 0.
    * By default, or if the data object does not have an 'id' or its value is not greater than 0, HTTP POST is used.
    *
    * @returns The SaveRequest instance for method chaining.
    */
    useHttpPutWhenId() {
        if (this.vo.id && this.vo.id > 0) {
            this.usePut = true;
        }
        return this;
    }
}

class CoffeeRequestDelete {
    constructor(httpClient, url, identifier) {
        this.httpClient = httpClient;
        this.queryParameters = Array();
        this.authorizationToken = null;
        this.url = url;
        this.identifier = identifier;
    }
    /**
     * Appends a new segment to the current URL of the request.
     * This method allows for dynamic construction of endpoint URLs by concatenating
     * additional path segments. It's particularly useful when you need to build
     * nested or parameterized URLs programmatically.
     *
     * @param newUrlSegment The new URL segment to be appended to the current URL.
     *
     * Example usage:
     * .withUrlSegment('details').withUrlSegment('123') would transform the URL from "/base/path" to "/base/path/details/123"
     */
    withUrlSegment(newUrlSegment) {
        this.url = CoffeeUtil.concatUrl(this.url, newUrlSegment);
        return this;
    }
    /**
     * Adds a query parameter to the request's URL. If multiple query parameters are added,
     * they are concatenated with an '&' character. This method facilitates the dynamic addition
     * of query parameters to the request URL, allowing for complex queries and filtering options
     * to be specified programmatically.
     *
     * @param key The key of the query parameter to add.
     * @param value The value of the query parameter.
     * @returns {CoffeeRequestPostPut<T>} The instance of CoffeeRequestPostPut for method chaining.
     *
     * Example usage:
     * .withQueryParameter('page', '1').withQueryParameter('limit', '10')
     * would append "?page=1&limit=10" to the URL.
     */
    withQueryParameter(key, value) {
        this.queryParameters.push(`${key}=${value}`);
        return this;
    }
    /**
     * Sets the authorization token to be used in HTTP requests.
     * @param token - The authorization token.
     */
    useAuthorizationToken(token) {
        this.authorizationToken = token;
        return this;
    }
    /**
     * Prepares and returns an Observable for the HTTP request, allowing further chaining with RxJS operators.
     *
     * @returns {Observable<T>} An Observable that emits the result of the HTTP request.
     */
    prepare() {
        this.url = CoffeeUtil.concatUrl(this.url, this.identifier);
        const headers = this.getHeaders();
        if (this.queryParameters.length) {
            this.url = `${this.url}?${this.queryParameters.join("&")}`;
        }
        return this.httpClient.delete(this.url, { headers });
    }
    /**
     * Subscribes to the observable created by the HTTP request, initiating the request process.
     * The request is made using HTTP DELETE.
     *
     * @param observerOrNext A partial Observer or a next function to handle the emitted value.
     * @returns A subscription to the observable representing the request.
     */
    subscribe(observerOrNext) {
        return this.prepare().subscribe(observerOrNext);
    }
    getHeaders() {
        let headers = new HttpHeaders();
        if (this.authorizationToken) {
            headers = headers.set('Authorization', this.authorizationToken);
        }
        return headers;
    }
}

class CoffeeRequestDownload {
    constructor(httpClient, endpoint, model, isFormData = false) {
        this.httpClient = httpClient;
        this.model = model;
        this.isFormData = isFormData;
        this.authorizationToken = null;
        this.queryParameters = [];
        this.apiUrl = endpoint;
    }
    /**
     * Sets the authorization token to be used in HTTP requests.
     * @param token - The authorization token.
     */
    useAuthorizationToken(token) {
        this.authorizationToken = token;
        return this;
    }
    /**
    * @summary
    * Sets query Parameter
    * @example
    * .withQueryParameter('name', 'text')
    * .withQueryParameter((model) => model.name, 'text')
    */
    withQueryParameter(type, value) {
        this.queryParameters.push(withQueryParameter(type, value));
        return this;
    }
    /**
     * Downloads a file from the server and saves it with the given file name.
     *
     * @param fileNameWithExtension - The desired file name, including the file extension (e.g., 'file.xlsx').
     * @param useJsonContentType - Determines if 'application/json' should be used as the Content-Type.
     * @returns An Observable that completes when the file has been downloaded.
     *
     * Usage example:
     * .downloadFileUsingPut('file.xlsx').subscribe(() => {
     *   console.log('File downloaded successfully');
     * });
     */
    downloadFileUsingPut(fileNameWithExtension, useJsonContentType = false) {
        return this.downloadFile('PUT', fileNameWithExtension, useJsonContentType);
    }
    /**
     * Downloads a file from the server and saves it with the given file name.
     *
     * @param fileNameWithExtension - The desired file name, including the file extension (e.g., 'file.xlsx').
     * @param useJsonContentType - Determines if 'application/json' should be used as the Content-Type.
     * @returns An Observable that completes when the file has been downloaded.
     *
     * Usage example:
     * .downloadFileUsingPost('file.xlsx').subscribe(() => {
     *   console.log('File downloaded successfully');
     * });
     */
    downloadFileUsingPost(fileNameWithExtension, useJsonContentType = false) {
        return this.downloadFile('POST', fileNameWithExtension, useJsonContentType);
    }
    /**
    * Downloads a file from the server using either PUT or POST method and saves it with the given file name.
    *
    * @param method - The HTTP method to use ('PUT' or 'POST').
    * @param fileNameWithExtension - The desired file name, including the file extension (e.g., 'file.xlsx').
    * @param useJsonContentType - Determines if 'application/json' should be used as the Content-Type.
    * @returns An Observable that completes when the file has been downloaded.
    *
    * Usage example:
    * .downloadFile('PUT', 'url', form, 'file.xlsx').subscribe(() => {
    *   console.log('File downloaded successfully');
    * });
    */
    downloadFile(method, fileNameWithExtension, useJsonContentType = false) {
        let headers = this.getHeaders(useJsonContentType);
        const url = this.parseUrl();
        const data = this.isFormData ? CoffeeUtil.convertModelToFormData(this.model) : this.model;
        const requestObservable = method === 'PUT'
            ? this.httpClient.put(url, data, { headers, responseType: 'blob' })
            : this.httpClient.post(url, data, { headers, responseType: 'blob' });
        return requestObservable.pipe(map((data) => {
            const downloadUrl = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileNameWithExtension;
            document.body.appendChild(link); // Ensure the link is in the document
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
            link.remove(); // Clean up the link element
            return data;
        }));
    }
    getHeaders(useJsonContentType) {
        let headers = new HttpHeaders();
        if (this.authorizationToken) {
            headers = headers.set('Authorization', this.authorizationToken);
        }
        if (!this.isFormData) {
            const contentType = useJsonContentType ? 'application/json' : 'application/octet-stream';
            headers = headers.set('Content-Type', contentType);
        }
        // If isFormData is true, do not set the Content-Type header. Let the browser set it to multipart/form-data with the correct boundary.
        return headers;
    }
    parseUrl(suffixEndpoint, pagination) {
        let params = [];
        let url = this.extractLastUrl(this.apiUrl);
        const filters = this.queryParameters.filter(param => param.type == 'filter');
        const sorts = this.queryParameters.filter(param => param.type == 'sort');
        const queryParameters = this.queryParameters.filter(param => param.type == 'parameter');
        if (filters.length) {
            params.push(`filters=${filters.map(filter => filter.expression).join(',')}`);
        }
        if (sorts.length) {
            params.push(`sorts=${sorts.map(sort => sort.expression).join(',')}`);
        }
        if (pagination && pagination.currentPage > -1) {
            params.push(`currentPage=${pagination.currentPage}`);
        }
        if (pagination && pagination.pageSize > -1) {
            params.push(`pageSize=${pagination.pageSize}`);
        }
        if (queryParameters.length) {
            params = params.concat(queryParameters.map(param => param.expression));
        }
        if (!suffixEndpoint?.startsWith("http")) {
            url += suffixEndpoint ?? '';
        }
        else {
            url = suffixEndpoint ?? '';
        }
        if (params.length > 0) {
            return url + '?' + params.join('&');
        }
        return url;
    }
    extractLastUrl(apiUrl) {
        const urls = apiUrl.split('http').filter(segment => segment.trim() !== '');
        if (urls.length > 0) {
            // Append 'http' back to the URL fragment unless it's already a complete URL segment
            return 'http' + urls[urls.length - 1];
        }
        return apiUrl; // Fallback to the original URL if no 'http' was found
    }
}

class CoffeeRequest {
    constructor(httpClient, encrypt) {
        this.httpClient = httpClient;
        this.encrypt = encrypt;
        this.config = inject(CONFIG);
        this.baseEndpoint = '';
        this.baseEndpoint = this.config ? this.config.baseApiUrl : "";
    }
    /**
     * @example
     * .get<User>('/user/info', where((model: Collection) => model.name, '==', 'john'), whereDate('createdAt', 'day', 28))
     * .returnListOf(User)
     * .subscribe({})
     * // or
     * .get<User>('/user/info')
     * .where((model: Collection) => model.name, '==', 'john') // filter with type
     * .whereDate('createdAt', 'day', 28) // filter with no type
     * .returnListOf(User)
     * .subscribe({})
     *
     */
    get(endpoint, ...args) {
        const url = CoffeeUtil.concatUrl(this.baseEndpoint, endpoint);
        return new CoffeeRequestGet(this.httpClient, url, ...args);
    }
    /**
     * Create/Update a value on a specific endpoint, based on property id
     * @param endpoint endpoint url "/myendpoint"
     * @param vo data to save
     * @param isFormData whether to send the data as form data or json
     */
    save(endpoint, vo, isFormData = false) {
        const url = CoffeeUtil.concatUrl(this.baseEndpoint, endpoint);
        return new CoffeeRquestSave(this.httpClient, this.encrypt, url, vo, isFormData, this.config);
    }
    /**
     * Create a new register on a specific endpoint
     * @param endpoint endpoint url "/myendpoint"
     * @param vo data to save
     * @param isFormData whether to send the data as form data or json
     */
    create(endpoint, vo, isFormData = false) {
        const url = CoffeeUtil.concatUrl(this.baseEndpoint, endpoint);
        return new CoffeeRequestPostPut(this.httpClient, this.encrypt, url, vo, false, isFormData, this.config);
    }
    /**
     * Update register on a specific endpoint
     * @param endpoint endpoint url "/myendpoint"
     * @param vo data to save
     * @param isFormData whether to send the data as form data or json
     */
    update(endpoint, vo, isFormData = false) {
        const url = CoffeeUtil.concatUrl(this.baseEndpoint, endpoint);
        return new CoffeeRequestPostPut(this.httpClient, this.encrypt, url, vo, true, isFormData, this.config);
    }
    /**
     * Delete register on a specific endpoint
     * @param endpoint endpoint url "/myendpoint"
     * @param identifier the identifier to use as key for deleting
     */
    delete(endpoint, identifier) {
        const url = CoffeeUtil.concatUrl(this.baseEndpoint, endpoint);
        return new CoffeeRequestDelete(this.httpClient, url, identifier);
    }
    /**
     * requests a file from the server.
     *
     * @param endpoint - The url of the endpoint.
     * @param model - The model (e.g { file : file }).
     * @param isFormData whether to send the data as form data or json
     *
     */
    requestDownload(endpoint, model, isFormData = false) {
        const url = CoffeeUtil.concatUrl(this.baseEndpoint, endpoint);
        return new CoffeeRequestDownload(this.httpClient, url, model, isFormData);
    }
}

var AuthUtils;
(function (AuthUtils) {
    AuthUtils.storageKey = 'sk';
    AuthUtils.userKey = 'usk';
    AuthUtils.clearTokens = () => {
        localStorage.clear();
    };
    AuthUtils.saveToken = (token) => {
        localStorage.setItem(AuthUtils.storageKey, token);
    };
    AuthUtils.getToken = () => {
        return localStorage.getItem(AuthUtils.storageKey);
    };
    AuthUtils.setUserProperty = (user, property, value) => {
        if (!user || !user.id) {
            return;
        }
        let savedValue = {};
        try {
            var storageValue = localStorage.getItem(AuthUtils.userKey);
            if (storageValue) {
                savedValue = JSON.parse(storageValue);
            }
        }
        catch { }
        savedValue[user.id + '_' + property] = JSON.stringify(value);
        localStorage.setItem(AuthUtils.userKey, JSON.stringify(savedValue));
        user[property] = value;
    };
    AuthUtils.getUserProperty = (user, property) => {
        if (!user || !user.id) {
            return null;
        }
        let savedValue = {};
        try {
            var storageValue = localStorage.getItem(AuthUtils.userKey);
            if (storageValue) {
                savedValue = JSON.parse(storageValue);
            }
            return JSON.parse(savedValue[user.id + '_' + property]);
        }
        catch {
            return null;
        }
    };
})(AuthUtils || (AuthUtils = {}));

class CoffeeAuthSocial {
}

class CoffeeSocialRequest {
    constructor(config, httpClient, request, msalService) {
        this.config = config;
        this.httpClient = httpClient;
        this.request = request;
        this.msalService = msalService;
        this.linkedInAuthApiUrl = "https://www.linkedin.com/oauth/v2/authorization";
        this.microsoftPhotoUrl = "https://graph.microsoft.com/v1.0/me/photo/$value";
    }
    /**
     * Initiates the Google sign-in process.
     *
     * This method relies on the ngx-coffee-social-google-button component, which
     * triggers the `onResponse` event upon receiving the user's authentication data.
     * The method then processes this data to complete the sign-in flow.
     *
     * @param {CoffeeAuthSocial} model - The user's authentication data received from Google.
     * @returns {Observable<boolean>} - An observable that emits `true` if the sign-in process is successful,
     *                                  or an error if the sign-in process fails.
     *
     * @example
     * // Example usage in a component
     * onGoogleSignInResponse(response: CoffeeAuthSocial): void {
     *     this.coffeeService.auth().social.signInWithGoogle(response).subscribe({
     *         next: (success) => {
     *             console.log('Google sign-in successful:', success);
     *         },
     *         error: (error) => {
     *             console.error('Google sign-in failed:', error);
     *         }
     *     });
     * }
     *
     * @see {@link CoffeeSocialGoogleButtonComponent} for the component that triggers this method.
     */
    signInWithGoogle(model) {
        return new Observable((observer) => {
            this._signInWithSocialMedia('Google', model)
                .subscribe({
                next: () => {
                    observer.next(true);
                    observer.complete();
                },
                error: (error) => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
    /**
     * Initiates the Microsoft sign-in process using the MSAL service. This method
     * handles the entire flow of authenticating with Microsoft, including opening
     * a popup for user consent, retrieving the user profile, and handling any errors
     * that might occur during the process.
     *
     * @returns {Observable<boolean>} - An observable that emits `true` if the sign-in process is successful.
     */
    signInWithMicrosoft() {
        return new Observable((observer) => {
            this._signInWithMicrosoft().subscribe({
                next: (model) => {
                    this._signInWithSocialMedia('Microsoft', model)
                        .subscribe({
                        next: () => {
                            observer.next(true);
                            observer.complete();
                        },
                        error: (error) => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
                },
                error: (error) => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
    /**
     * Initiates the LinkedIn sign-in process.
     * Depending on the configuration, it either redirects the user to the LinkedIn authorization page
     * or opens it in a new popup window.
     *
     * @returns {Observable<boolean>} - An observable that emits `true` if the window is opened or user is redirected successfully.
     *
     * @example
     * signInWithLinkedIn().subscribe(
     *     success => console.log('Sign-in initiated:', success),
     *     error => console.log('Sign-in initiation failed:', error)
     * );
     *
     * @see {@link https://learn.microsoft.com/pt-br/linkedin/shared/authentication/authorization-code-flow}
     */
    signInWithLinkedIn() {
        if (!this.config?.auth?.linkedIn) {
            throw Error('LinkedIn Authentication Configuration Missing: Ensure that the "auth"' +
                ' configuration property is provided when utilizing "CoffeeModule.forRoot({})"' +
                ' and that it includes "linkedIn" authentication settings.');
        }
        let url = `${this.linkedInAuthApiUrl}?`;
        url += `response_type=code&`;
        url += `client_id=${this.config.auth.linkedIn.clientId}&`;
        url += `scope=${this.config.auth.linkedIn.scope ?? 'r_emailaddress,r_liteprofile'}&`;
        url += `redirect_uri=${this.config.auth.linkedIn.redirectUrl}`;
        return new Observable((observer) => {
            if (!this.config.auth?.linkedIn?.openInPopup) {
                window.open(url, '_self');
                observer.next(true);
                observer.complete();
                return;
            }
            const win = window.open(url, '_blank', `scrollbars=yes,resizable=yes,width=500,height=500`);
            const timer = setInterval(() => {
                if (win.closed) {
                    const token = win.linkCode;
                    clearInterval(timer);
                    if (token) {
                        AuthUtils.saveToken(token);
                        observer.next(true);
                        observer.complete();
                    }
                    else {
                        observer.error();
                        observer.complete();
                    }
                }
            }, 500);
        });
    }
    /**
    * Validates the LinkedIn sign-in by exchanging the received `code` for an access token,
    * which is then stored for subsequent API requests.
    *
    * @param {string} code - The authorization code received from LinkedIn.
    * @param {any} [bodyData=null] - Optional. Additional body data to send in the request.
    *
    * @returns {Observable<boolean>} - An observable that emits `true` if the token is successfully obtained and saved.
    *
    * @example
    * validateLinkedInSignIn(code).subscribe(
    *     success => console.log('Token validated:', success),
    *     error => console.log('Token validation failed:', error)
    * );
    */
    validateLinkedInSignIn(code, bodyData = null) {
        const url = this.config?.baseApiUrl;
        bodyData ? bodyData : { login: 'linkedIn', password: 'linkedIn' };
        return this.httpClient.post(`${url}/user/authenticate?linkedInCode=` + code, bodyData)
            .pipe(map$1((data) => {
            window.linkCode = data.token;
            AuthUtils.saveToken(data.token);
            return true;
        }));
    }
    /**
     * A private method that performs the Microsoft login operation. It uses the MSAL
     * service to open a login popup and requests access tokens for the specified scopes.
     * On successful login, it fetches the user's profile picture from Microsoft Graph API.
     *
     * @returns {Observable<CoffeeAuthSocial>} - An observable that emits the authenticated user's data.
     */
    _signInWithMicrosoft() {
        return new Observable((observer) => {
            let model = new CoffeeAuthSocial();
            const scopes = this.config.auth?.microsoft?.scopes ?? ['User.Read', 'User.ReadBasic.All'];
            this.msalService.instance.handleRedirectPromise()
                .then(() => {
                this.msalService.loginPopup({
                    scopes: scopes
                }).subscribe({
                    next: (popupResult) => {
                        model = {
                            id: popupResult.account.tenantId,
                            email: popupResult.account.username,
                            name: popupResult.account.name ?? '',
                            token: popupResult.account.idToken ?? '',
                        };
                        const accessTokenRequest = {
                            scopes: scopes,
                            account: popupResult.account
                        };
                        // GETTING TOKEN TO GET USER PROFILE IMAGE
                        this.msalService.acquireTokenSilent(accessTokenRequest)
                            .subscribe({
                            next: (response) => {
                                const headers = {
                                    Authorization: `Bearer ${response.accessToken}`
                                };
                                this.httpClient.get(this.microsoftPhotoUrl, { headers, responseType: 'blob' })
                                    .subscribe({
                                    next: (image) => {
                                        model.image = image;
                                    },
                                    complete: () => {
                                        observer.next(model);
                                        observer.complete();
                                    }
                                });
                            },
                            // FAILED TO ACQUIRE TOKEN
                            error: () => {
                                observer.next(model);
                                observer.complete();
                            }
                        });
                    },
                    // FAILED TO OPEN POPUP
                    error: (error) => {
                        observer.error({
                            code: "POPUP_AUTH_FAILURE",
                            error: error,
                            message: "Authentication popup failed to open. Please ensure " +
                                "Microsoft authentication is correctly configured in CoffeeModule," +
                                " and verify that the provided client ID and tenant information are accurate."
                        });
                        observer.complete();
                    }
                });
            })
                .catch((error) => {
                observer.error({
                    code: "REDIRECT_FAILURE",
                    error: error,
                    message: "Authentication popup redirect failed. Please ensure " +
                        "Microsoft authentication is correctly configured in CoffeeModule," +
                        " and verify that the provided client ID and tenant information are accurate."
                });
                observer.complete();
            });
        });
    }
    /**
     * Handles the submission of social media authentication data to the server.
     * This method is responsible for sending the user's information, including
     * their profile picture, to a designated server endpoint for either Google or
     * Microsoft sign-in processes.
     *
     * @param {string} type - The type of social media ('Google' or 'Microsoft').
     * @param {CoffeeAuthSocial} user - The user's authentication data.
     *
     * @returns {Observable<boolean>} - An observable that emits `true` if the data is successfully submitted.
     */
    _signInWithSocialMedia(type, user) {
        return new Observable((observer) => {
            var file = null;
            if (user.image != null) {
                file = new File([user.image], `${user.id}.png`, {
                    type: user.image.type,
                    lastModified: new Date().getTime()
                });
            }
            this.request.save(`user/socialmedia/${type}`, {
                'name': user.name,
                'email': user.email,
                'photoUrl': user.photoUrl,
                'serverAuthCode': user.token,
                'photo': {
                    file: file
                },
                'id': user.id
            }, true)
                .subscribe({
                next: (snapshot) => {
                    if (snapshot?.token) {
                        AuthUtils.saveToken(snapshot.token);
                    }
                    observer.next(true);
                    observer.complete();
                },
                error: (error) => {
                    observer.error({
                        code: 'SOCIAL_MEDIA_AUTH_ENDPOINT_ERROR',
                        error: error,
                        message: `There was an issue with the social media authentication` +
                            ` process for ${type}. Ensure the endpoint "user/socialmedia/${type}"` +
                            ` is properly configured and accessible. This error may also occur if` +
                            ` the server-side handling of the authentication data for ${type} is not set up correctly.`
                    });
                    observer.complete();
                },
            });
        });
    }
}

class CoffeeAuthRequest {
    constructor(config, httpClient, coffeeService, msalService, type) {
        this.config = config;
        this.httpClient = httpClient;
        this.coffeeService = coffeeService;
        this.msalService = msalService;
        this.type = type;
        this.currentUserLock = false;
        this.queryParams = {};
    }
    get social() {
        if (!this.socialRequest) {
            this.socialRequest = new CoffeeSocialRequest(this.config, this.httpClient, this.coffeeService, this.msalService);
        }
        return this.socialRequest;
    }
    /**
     * Chains a query parameter to the request made by the `getCurrentUser` method.
     *
     * This method allows you to add additional query parameters to the request URL
     * when calling `getCurrentUser`. The parameters are stored in the instance and
     * appended to the URL during the API call.
     *
     * @param {string} key - The name of the query parameter.
     * @param {string} value - The value of the query parameter.
     *
     * @returns {CoffeeAuthRequest<T>} - The instance of the class, allowing for method chaining.
     *
     * @example
     * // Adds a query parameter 'exampleKey' with the value 'exampleValue' to the request.
     * authRequest.withQueryParameter('exampleKey', 'exampleValue');
     *
     * @see getCurrentUser
     */
    withQueryParameter(key, value) {
        this.queryParams[key] = value;
        return this;
    }
    /**
     * Set a value in the storage, associated with a key and the logged-in user.
     *
     * This method first retrieves the current user and then sets the provided value
     * in the storage, associated with the provided key and the user.
     *
     * @param {string} key - The key with which the value will be associated in the storage.
     * @param {any} value - The value to be stored.
     *
     * @returns {Observable<void>} - An Observable that completes when the value has been set.
     */
    setStorageValue(key, value) {
        return new Observable(observer => {
            this.getCurrentUser().subscribe(() => {
                AuthUtils.setUserProperty(this.currentUser, key, value);
                observer.next();
                observer.complete();
            });
        });
    }
    /**
     * Retrieve a value from the storage, based on a key and the logged-in user.
     *
     * This method first retrieves the current user and then gets the value
     * associated with the provided key from the storage.
     *
     * @param {string} key - The key whose associated value is to be retrieved.
     *
     * @returns {Observable<any>} - An Observable that emits the retrieved value.
     */
    getStorageValue(key) {
        return new Observable(observer => {
            this.getCurrentUser().subscribe(() => {
                const value = AuthUtils.getUserProperty(this.currentUser, key);
                observer.next(value);
                observer.complete();
            });
        });
    }
    /**
     * Authenticates a user using their login and password.
     *
     * This method initiates a POST request to the "user/authenticate" endpoint, carrying
     * the authentication details provided in the `auth` parameter. Before sending, the
     * authentication data is encrypted using the public key fetched by the `encrypt` service.
     * The encryption process is transparent to the caller; if encryption succeeds, the request
     * includes an 'encrypted' header set to 'true'. If encryption is not possible (e.g., due to
     * missing encryption configuration or keys), the data is sent as is, without encryption.
     *
     * The server is expected to respond with an object containing both the user data
     * and an authentication token. This method extracts the token, saving it for future
     * requests, and returns the user data wrapped in an Observable for further processing
     * or direct subscription.
     *
     * Note: Encryption failures are handled gracefully by logging an error and proceeding
     * without encryption, but this behavior can be adjusted based on security requirements.
     *
     * @param {CoffeeAuth} auth - The authentication data object, typically containing user login
     *                            and password fields.
     *
     * @returns {Observable<T>} - An Observable emitting the authenticated user data upon successful
     *                            authentication. The user data type, `T`, is determined by the generic
     *                            parameter of the class. In case of an error during the HTTP request or
     *                            the encryption process, the Observable will emit an error.
     */
    siginWithLoginPassword(auth) {
        return this.coffeeService.create('user/authenticate', auth)
            .withEncryption()
            .prepare()
            .pipe(map$1(data => {
            const typedData = data;
            AuthUtils.saveToken(typedData.token);
            const user = this.type ? new this.type(typedData.user) : typedData.user;
            return user;
        }));
    }
    /**
    * Authenticate by code, it will use "user"
    * endpoint(POST) and concatenate with the url,
    * this endpoint must return { user: ..., token: .... }
    */
    siginWithCode(url, code) {
        return this.coffeeService.create('user', null, true)
            .withUrlSegment(url)
            .withQueryParameter('code', code)
            .withEncryption()
            .prepare()
            .pipe(map$1(data => {
            const typedData = data;
            AuthUtils.saveToken(typedData.token);
            const user = this.type ? new this.type(typedData.user) : typedData.user;
            return user;
        }));
    }
    /**
     * Change the password of the current user.
     *
     * This method sends a PUT request to the "user/changepassword" endpoint with
     * the new password. The endpoint is expected to update the password of the
     * currently authenticated user.
     *
     * @param {string} newPassword - The new password for the user.
     *
     * @returns {Observable<T>} - An Observable that emits the current user data.
     */
    changePassword(newPassword) {
        return this.coffeeService.update('user/changepassword', newPassword, true)
            .withEncryption()
            .prepare()
            .pipe(map$1(() => {
            return this.currentUser;
        }));
    }
    /**
     * Check if the current user is logged in.
     *
     * This method checks if a token exists and if the current user data is available,
     * indicating that a user is logged in.
     *
     * @returns {Observable<boolean>} - An Observable that emits `true` if a user is logged in, otherwise `false`.
     */
    isLoggedIn() {
        const hasToken = AuthUtils.getToken() != null ? true : false;
        return new Observable(observer => {
            if (!hasToken) {
                observer.next(false);
                observer.complete();
                return;
            }
            if (!this.currentUser) {
                this.getAuthInfo().subscribe({
                    next: user => {
                        if (!user) {
                            observer.next(false);
                        }
                        else {
                            observer.next(true);
                        }
                        observer.complete();
                    },
                    error: () => {
                        observer.next(false);
                        observer.complete();
                    }
                });
            }
            else {
                observer.next(true);
                observer.complete();
            }
        });
    }
    /**
     * Retrieve the current logged-in user data.
     *
     * This method retrieves the data of the current logged-in user. If the user data
     * is cached, it returns the cached data. Otherwise, it sends a GET request to
     * the "user/authenticated/info" endpoint to retrieve the user data.
     *
     * @returns {Observable<T | null>} - An Observable that emits the current user data, or `null` if no user is logged in.
     */
    getCurrentUser() {
        if (!AuthUtils.getToken()) {
            return of(null);
        }
        if (this.currentUser) {
            return of(this.currentUser);
        }
        if (this.currentUserCache$) {
            return this.currentUserCache$;
        }
        if (!this.currentUserLock) {
            this.currentUserLock = true;
            this.currentUserCache$ = this.getAuthInfo().pipe(tap((user) => {
                this.currentUser = user;
                this.currentUserLock = false;
                this.currentUserCache$ = undefined;
            }), catchError((err) => {
                this.currentUserCache$ = undefined;
                this.currentUserLock = false;
                this.currentUser = null;
                return throwError(() => err);
            }), finalize(() => {
                if (!this.currentUser) {
                    this.currentUserLock = false;
                }
            }));
            return this.currentUserCache$;
        }
        else {
            // If another request is in progress, wait for it to complete
            return interval(50).pipe(startWith(0), mergeMap(() => {
                if (this.currentUser) {
                    return of(this.currentUser);
                }
                else {
                    return EMPTY;
                }
            }), take(1));
        }
    }
    /**
     * Refresh the current logged-in user data.
     *
     * This method clears the cached user data and retrieves it again, ensuring that
     * the client has the most up-to-date user data.
     *
     * @returns {Observable<T | null>} - An Observable that emits the refreshed user data, or `null` if no user is logged in.
     */
    refreshCurrentUser() {
        this.currentUser = null; // Reset the current user
        return this.getCurrentUser();
    }
    /**
     * Log out the current user.
     *
     * This method clears the stored tokens and the cached user data, effectively
     * logging out the user.
    */
    async logout() {
        AuthUtils.clearTokens();
        if (this.msalService.instance.getAllAccounts().length > 0) {
            await this.msalService.instance.logout({
                postLogoutRedirectUri: this.config.auth?.microsoft?.postLogoutRedirectUri ?? window.location.origin
            });
        }
        this.currentUser = null;
    }
    getAuthInfo() {
        const baseEndPoint = this.config ? this.config.baseApiUrl : "";
        let url = CoffeeUtil.concatUrl(baseEndPoint, 'user/authenticated/info');
        // Append query parameters to the URL
        const queryString = Object.keys(this.queryParams)
            .map(key => `${key}=${encodeURIComponent(this.queryParams[key])}`)
            .join('&');
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        return this.httpClient
            .get(url)
            .pipe(map$1(data => {
            this.currentUser = this.type ? new this.type(data) : data;
            // try to restore save data
            try {
                const keys = Object.keys(this.currentUser);
                for (const key of keys) {
                    const storageValue = AuthUtils.getUserProperty(this.currentUser, key);
                    if (!storageValue) {
                        continue;
                    }
                    this.currentUser[key] = storageValue;
                    this.currentUser = this.type ? new this.type(this.currentUser) : this.currentUser;
                }
            }
            catch { }
            return this.currentUser;
        }), finalize(() => {
            if (!this.currentUser) {
                this.currentUserLock = false;
            }
        }), catchError((err) => {
            this.currentUserLock = false;
            this.currentUserCache$ = undefined;
            this.currentUser = null;
            return throwError(() => err);
        }));
    }
}

class CoffeeFormUtil {
    /**
     * Ensure that the form is validated
     */
    validateForm(formGroup) {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof FormControl) {
                control.markAsTouched({ onlySelf: true });
                control.updateValueAndValidity();
            }
            else if (control instanceof FormGroup) {
                this.validateForm(control);
            }
        });
    }
}

class CoffeeFileUtil {
    /**
     * Ensure that the form is validated
     */
    fileToBase64(data) {
        return new Observable(observer => {
            var reader = new FileReader();
            reader.readAsDataURL(data.file);
            reader.onload = () => {
                observer.next(reader.result);
                observer.complete();
            };
            reader.onerror = (error) => {
                observer.error(error);
                observer.complete();
            };
        });
    }
    base64ToFile(base64String, fileNameWithExtension) {
        return new Observable(observer => {
            const arr = base64String.split(',');
            if (!arr) {
                observer.error("Invalid base 64 string");
                observer.complete();
                return;
            }
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            observer.next({ file: new File([u8arr], fileNameWithExtension, { type: mime }) });
            observer.complete();
        });
    }
}

class CoffeeEncryptService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    async encrypt(data) {
        await this.fetchKey();
        if (!this.publicKey) {
            return false;
        }
        const aesKey = CryptoJS.lib.WordArray.random(128 / 8).toString(CryptoJS.enc.Hex);
        const aesIV = CryptoJS.lib.WordArray.random(128 / 8).toString(CryptoJS.enc.Hex);
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), CryptoJS.enc.Hex.parse(aesKey), {
            iv: CryptoJS.enc.Hex.parse(aesIV),
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        }).toString();
        const encryptedKey = await this.encryptWithPublicKey(aesKey + ':' + aesIV);
        if (!encryptedKey) {
            return false;
        }
        return { k: encryptedKey, d: encryptedData };
    }
    async encryptWithPublicKey(data) {
        try {
            const enc = new TextEncoder();
            // Convert PEM to ArrayBuffer
            const pemHeader = "-----BEGIN PUBLIC KEY-----";
            const pemFooter = "-----END PUBLIC KEY-----";
            let pemContents = this.publicKey.replace(pemHeader, '').replace(pemFooter, '');
            pemContents = pemContents.replace(/\s/g, '');
            const binaryDerString = atob(pemContents);
            const binaryDer = this.str2ab(binaryDerString);
            // Import the public key
            const cryptoKey = await window.crypto.subtle.importKey('spki', binaryDer, {
                name: "RSA-OAEP",
                hash: { name: "SHA-256" }, // Specify the hash algorithm for OAEP
            }, true, ["encrypt"]);
            // Encrypt the data
            const encryptedData = await window.crypto.subtle.encrypt({
                name: "RSA-OAEP",
                hash: { name: "SHA-256" }
            }, // Explicitly asserting the type
            cryptoKey, enc.encode(data));
            // Convert Uint8Array to binary string
            const binaryString = Array.from(new Uint8Array(encryptedData), byte => String.fromCharCode(byte)).join('');
            return btoa(binaryString);
        }
        catch (error) {
            console.error('Encryption error:', error);
            return null;
        }
    }
    str2ab(str) {
        const buf = new ArrayBuffer(str.length);
        const bufView = new Uint8Array(buf);
        for (let i = 0, strLen = str.length; i < strLen; i++) {
            bufView[i] = str.charCodeAt(i);
        }
        return buf;
    }
    async fetchKey() {
        if (this.publicKey) {
            return;
        }
        try {
            const options = { responseType: 'text' };
            const snapshot = await firstValueFrom(this.http.get(this.config.baseApiUrl + '/coffee/keys/public', options));
            this.publicKey = snapshot;
        }
        catch (error) {
            if (this.config.disableEncryptErrorLogs || this.config.disableEncryption) {
                return;
            }
            console.error("It appears that encryption has not been configured on the server. " +
                "To ensure your authentication data is securely transmitted, please verify the " +
                "encryption setup on the server side. If this issue persists, consult the server " +
                "configuration documentation. Alternatively, if you prefer not to see this message again, " +
                "you can disable encryption error logs by configuring the CoffeeModule with " +
                "`CoffeeModule.forRoot({ disableEncryptErrorLogs: true })` in your application setup.");
        }
    }
}
CoffeeEncryptService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeEncryptService, deps: [{ token: i1.HttpClient }, { token: CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CoffeeEncryptService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeEncryptService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeEncryptService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CONFIG]
                }] }]; } });

class CoffeeService extends CoffeeRequest {
    constructor(httpClient, encrypt, msalService) {
        super(httpClient, encrypt);
        this.msalService = msalService;
    }
    /**
     * Service to handle authentication
     * @example auth<User>(User).isLoggedIn();
     */
    auth(type) {
        if (!this.authRequest) {
            this.authRequest = new CoffeeAuthRequest(this.config, this.httpClient, this, this.msalService, type);
        }
        return this.authRequest;
    }
    /**
     * Service to handle forms utils
     */
    forms() {
        if (!this.formUtil) {
            this.formUtil = new CoffeeFormUtil();
        }
        return this.formUtil;
    }
    fileUtils() {
        return new CoffeeFileUtil();
    }
}
CoffeeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeService, deps: [{ token: i1.HttpClient }, { token: CoffeeEncryptService }, { token: i3.MsalService }], target: i0.ɵɵFactoryTarget.Injectable });
CoffeeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: CoffeeEncryptService }, { type: i3.MsalService }]; } });

class CoffeeInterceptor {
    constructor() {
        this.config = inject(CONFIG);
    }
    intercept(request, next) {
        return next
            .handle(this.addTokenToRequest(request, AuthUtils.getToken() ?? '', this.config))
            .pipe(map((response) => {
            return response;
        }), catchError$1(async (response) => {
            let error = response;
            if (response.error) {
                error = response.error;
                if (error.error) {
                    error = error.error;
                }
            }
            if (response instanceof HttpErrorResponse) {
                // Forbidden user, insufficient privileges
                if (response.status === 403 || response.status !== 401) {
                    return await lastValueFrom(throwError(() => error));
                }
            }
            return await lastValueFrom(throwError(() => error));
        }));
    }
    addTokenToRequest(request, token, config) {
        // Check if the request already has an Authorization header and if the URL matches the baseApiUrl
        const alreadyHasAuthHeader = request.headers.has('Authorization');
        const isRequestToApi = request.url.indexOf(config.baseApiUrl) >= 0;
        if (!alreadyHasAuthHeader && token && isRequestToApi) {
            // Clone the request to add the new header
            return request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        }
        // Return the original request if no changes are needed
        return request;
    }
}
CoffeeInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CoffeeInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeInterceptor, decorators: [{
            type: Injectable
        }] });

class CoffeeGlobalErrorService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.errorCache = new Map();
        this.errorCacheTime = 10000; // Time in milliseconds to cache the same error
    }
    handleError(error) {
        if (this.config.disableLogs || !this.config.baseApiUrl) {
            throw error;
        }
        const errorSignature = this.getErrorSignature(error);
        if (this.isErrorRecentlyLogged(errorSignature)) {
            throw error;
        }
        this.logError(error, errorSignature);
        throw error;
    }
    logError(error, errorSignature) {
        const message = error.message ? error.message : error.toString();
        const stack = error.stack ? error.stack : '';
        const currentUrl = window.location.href;
        const timestamp = new Date().toISOString();
        const logLevel = 'error';
        const errorPayload = {
            message: message,
            stack: stack,
            type: 'Angular',
            requestUrl: currentUrl,
            timestamp: timestamp,
            logLevel: logLevel
        };
        this.errorCache.set(errorSignature, Date.now());
        this.http.post(this.config.baseApiUrl + '/coffee/log/error', errorPayload)
            .subscribe(() => { });
    }
    getErrorSignature(error) {
        return error.message;
    }
    isErrorRecentlyLogged(errorSignature) {
        const lastLoggedTime = this.errorCache.get(errorSignature);
        if (lastLoggedTime) {
            const timeElapsed = Date.now() - lastLoggedTime;
            if (timeElapsed < this.errorCacheTime) {
                return true;
            }
        }
        return false;
    }
}
CoffeeGlobalErrorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeGlobalErrorService, deps: [{ token: i1.HttpClient }, { token: CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CoffeeGlobalErrorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeGlobalErrorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeGlobalErrorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CONFIG]
                }] }]; } });

const microsoftFactoryConfig = (config) => {
    return new PublicClientApplication({
        auth: {
            clientId: config.auth?.microsoft?.clientId ?? '',
            redirectUri: config.auth?.microsoft?.redirectUri ?? '',
            authority: config.auth?.microsoft?.authority ?? ''
        }
    });
};
function initializeMsal(msalService) {
    return () => {
        return lastValueFrom(msalService.initialize());
    };
}
class CoffeeModule {
    static forRoot(config) {
        return {
            ngModule: CoffeeModule,
            providers: [
                CoffeeService,
                CoffeeEncryptService,
                {
                    provide: CONFIG,
                    useValue: config
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: CoffeeInterceptor,
                    multi: true
                },
                {
                    provide: ErrorHandler,
                    useClass: CoffeeGlobalErrorService
                },
                // MICROSOFT SETUP
                {
                    provide: MsalService,
                    useFactory: (config, location) => new MsalService(microsoftFactoryConfig(config), location),
                    deps: [CONFIG, Location]
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeMsal,
                    deps: [MsalService],
                    multi: true
                },
                {
                    provide: PublicClientApplication,
                    useFactory: microsoftFactoryConfig,
                    deps: [CONFIG]
                },
            ]
        };
    }
}
CoffeeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CoffeeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CoffeeModule, imports: [HttpClientModule] });
CoffeeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeModule, imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                    ]
                }]
        }] });

class CoffeeMapper {
    constructor(thisReference, json) {
        this.thisReference = thisReference;
        this.json = json;
        this.properties();
    }
    properties() {
        if (this.json != null) {
            const keys = Object
                .keys(this.thisReference)
                .filter(k => this.thisReference[k]?.constructor?.name != 'Object' &&
                this.thisReference[k]?.constructor?.name != 'Array');
            for (const key of keys) {
                this.thisReference[key] = this.json[key];
            }
        }
        return this;
    }
    /**
     * This method will create the instance of the object | array
     * @example Given the object { user: { }, animals: [{...}]}
     *
     * createInstance('user', User) results in => User {...}
     * createInstance('animals', Animal) results in => Animal [{...}]
     */
    createInstance(propertyName, type) {
        if (this.json != null) {
            const key = Object.keys(this.thisReference).find(k => k == propertyName);
            if (!key) {
                return this;
            }
            if (this.json[key]?.constructor?.name == 'Object') {
                if (this.json.hasOwnProperty(key)) {
                    this.thisReference[key] = new type(this.json[key]);
                }
            }
            else if (this.thisReference[key]?.constructor?.name == 'Array') {
                if (this.json.hasOwnProperty(key) && this.json[key]?.constructor?.name == 'Array') {
                    if (this.json[key].length &&
                        (typeof this.json[key][0] === "string" ||
                            typeof this.json[key][0] === "number")) {
                        this.thisReference[key] = this.json[key];
                    }
                    else {
                        this.thisReference[key] = this.json[key].map((m) => new type(m));
                    }
                }
            }
        }
        return this;
    }
}

class CoffeeModel {
    /**
     * @example map({ id: 1, name: 'string'})
     */
    map(model) {
        return new CoffeeMapper(this, model);
    }
}

class CoffeeFileUpload {
    constructor(init) {
        this.id = 0;
        this.createdAt = '';
        this.updatedAt = '';
        this.name = '';
        this.size = 0;
        this.extension = '';
        this.path = '';
        Object.assign(this, init);
    }
}

class SingleFileUploadComponent {
    set externalFile(file) {
        if (file) {
            this.onNewFile(file);
        }
    }
    constructor(coffeeService) {
        this.coffeeService = coffeeService;
        this.previewFile = null;
        this.uploadFailed = false;
        this.uploadedFile = null;
        this.optimizationInfo = '';
        this.uploadErrorMessage = 'Upload failed, please try again.';
        this.storageBucket = 'default';
        this.autoUpload = true;
        this.progressEmitter = new BehaviorSubject(0);
        this.onRemove = new Subject();
        this.onChange = new Subject();
        this.onNewCoffeeFileUploaded = new Subject();
        this.fileSelected = new EventEmitter();
    }
    ngAfterViewInit() {
        if (this.form && this.form.get(this.controlName)) {
            this.uploadSubscription = this.form.get(this.controlName).valueChanges.subscribe(value => {
                this.handleFileChange(value);
            });
            // Handle the initial value
            this.handleFileChange(this.form.get(this.controlName).value);
        }
    }
    ngOnDestroy() {
        if (this.uploadSubscription) {
            this.uploadSubscription.unsubscribe();
        }
        this.progressEmitter.complete();
    }
    get context() {
        return {
            click: () => this.triggerFileSelection(),
            optimizationInfo: this.optimizationInfo,
            progress: this.progressEmitter.value,
            preview: this.previewFile,
            isImage: () => this.isImage(this.previewFile),
            errorMessage: this.uploadFailed ? this.uploadErrorMessage : null,
            actions: {
                preview: () => {
                    if (this.previewFile && this.previewFile.id) {
                        if (this.isImage(this.previewFile)) {
                            this.showImageFullscreen();
                        }
                        else {
                            window.open(this.previewFile.path);
                        }
                    }
                },
                remove: () => {
                    if (this.uploadedFile) {
                        this.onRemove.next(this.uploadedFile);
                    }
                },
            }
        };
    }
    onFileSelected(event) {
        const inputElement = event.target;
        const files = inputElement.files;
        if (files && files.length > 0) {
            this.fileSelected.emit(files[0]);
            if (this.autoUpload) {
                this.onNewFile(files[0]);
            }
        }
        else {
            this.progressEmitter.next(0);
        }
    }
    resetUpload() {
        this.uploadFailed = false;
        this.progressEmitter.next(0);
        this.form.get(this.controlName)?.setValue(null);
        this.previewFile = null;
        this.uploadedFile = null;
        this.optimizationInfo = '';
        this.clearFileInput();
    }
    showImageFullscreen() {
        const fullscreenDiv = document.createElement('div');
        fullscreenDiv.style.position = 'fixed';
        fullscreenDiv.style.top = '0';
        fullscreenDiv.style.left = '0';
        fullscreenDiv.style.width = '100%';
        fullscreenDiv.style.height = '100%';
        fullscreenDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        fullscreenDiv.style.display = 'flex';
        fullscreenDiv.style.alignItems = 'center';
        fullscreenDiv.style.justifyContent = 'center';
        fullscreenDiv.style.zIndex = '999999';
        const imageElement = document.createElement('img');
        imageElement.src = this.previewFile.path;
        imageElement.style.maxWidth = '100%';
        imageElement.style.maxHeight = '100%';
        fullscreenDiv.appendChild(imageElement);
        document.body.appendChild(fullscreenDiv);
        fullscreenDiv.addEventListener('click', () => {
            document.body.removeChild(fullscreenDiv);
        });
    }
    isImage(file) {
        if (!file) {
            return false;
        }
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'jfif', 'tiff', 'webp', 'svg', 'ico'];
        const path = file.extension ? file.extension : file.path;
        // Check if the uploaded file has a valid extension
        if (path) {
            const paths = path.toLowerCase().split(".").map(e => e);
            const lowerCaseExtension = paths[paths.length - 1];
            return imageExtensions.includes(lowerCaseExtension);
        }
        return false;
    }
    onNewFile(file) {
        this.resetUpload();
        this.updateFilePreview(file);
        this.uploadFile(file);
        this.onChange.next({ inProgress: true });
    }
    triggerFileSelection() {
        this.fileInput.nativeElement.click();
    }
    getFileExtension(filename) {
        return filename.split('.').pop() || '';
    }
    getFileNameWithoutExtension(fileName) {
        const lastDotIndex = fileName.lastIndexOf('.');
        return lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    }
    handleFileChange(file) {
        if (file instanceof File) {
            this.onNewFile(file);
            return;
        }
        if (file && file.path) {
            this.uploadedFile = file;
            this.previewFile = file;
            this.progressEmitter.next(100);
            if (file.size) {
                const formattedSize = this.formatFileSize(file.size);
                this.optimizationInfo = `The image is ${formattedSize}.`;
            }
        }
    }
    updateFilePreview(file) {
        const reader = new FileReader();
        reader.onload = () => {
            this.previewFile = new CoffeeFileUpload({
                name: this.getFileNameWithoutExtension(file.name),
                size: file.size,
                extension: this.getFileExtension(file.name),
                path: reader.result,
                createdAt: new Date().toISOString()
            });
        };
        reader.readAsDataURL(file);
    }
    uploadFile(file) {
        // Simulate upload progress
        this.progressEmitter.next(1);
        let progress = 1;
        const interval = setInterval(() => {
            progress = Math.min(progress + 1, 99);
            this.progressEmitter.next(progress);
        }, 200);
        // Replace with actual upload logic
        this.coffeeService.save(`coffee/file/storage/${this.storageBucket}`, { file: file }, true)
            .subscribe({
            next: (data) => {
                clearInterval(interval);
                this.progressEmitter.next(100);
                this.form.get(this.controlName)?.setValue(data);
                this.previewFile = data;
                this.uploadedFile = data;
                this.updateOptimizationInfo(file.size, data.size);
                this.clearFileInput();
                this.onNewCoffeeFileUploaded.next(data);
                this.onChange.next(data);
            },
            error: () => {
                clearInterval(interval);
                this.progressEmitter.next(0);
                this.uploadFailed = true;
                this.uploadedFile = null;
                this.form.get(this.controlName)?.setValue(null);
                this.onChange.next(null);
            }
        });
    }
    updateOptimizationInfo(originalSize, newSize) {
        const original = this.formatFileSize(originalSize);
        const optimized = this.formatFileSize(newSize);
        this.optimizationInfo = `Optimized from ${original} to ${optimized}.`;
    }
    formatFileSize(bytes, decimals = 2) {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }
    clearFileInput() {
        if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
    }
}
SingleFileUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SingleFileUploadComponent, deps: [{ token: CoffeeService }], target: i0.ɵɵFactoryTarget.Component });
SingleFileUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: SingleFileUploadComponent, selector: "app-single-file-upload", inputs: { form: "form", controlName: "controlName", uploadErrorMessage: "uploadErrorMessage", storageBucket: "storageBucket", customTemplate: "customTemplate", autoUpload: "autoUpload", externalFile: "externalFile" }, outputs: { progressEmitter: "progressEmitter", onRemove: "onRemove", onChange: "onChange", onNewCoffeeFileUploaded: "onNewCoffeeFileUploaded", fileSelected: "fileSelected" }, viewQueries: [{ propertyName: "fileInput", first: true, predicate: ["fileInput"], descendants: true }], ngImport: i0, template: "<ng-content></ng-content>\r\n<input type=\"file\" #fileInput hidden aria-label=\"Input Selector\" name=\"file-upload-input\"\r\n(change)=\"onFileSelected($event)\" />" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SingleFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-single-file-upload', template: "<ng-content></ng-content>\r\n<input type=\"file\" #fileInput hidden aria-label=\"Input Selector\" name=\"file-upload-input\"\r\n(change)=\"onFileSelected($event)\" />" }]
        }], ctorParameters: function () { return [{ type: CoffeeService }]; }, propDecorators: { form: [{
                type: Input
            }], controlName: [{
                type: Input
            }], uploadErrorMessage: [{
                type: Input
            }], storageBucket: [{
                type: Input
            }], customTemplate: [{
                type: Input
            }], autoUpload: [{
                type: Input
            }], externalFile: [{
                type: Input
            }], progressEmitter: [{
                type: Output
            }], onRemove: [{
                type: Output
            }], onChange: [{
                type: Output
            }], onNewCoffeeFileUploaded: [{
                type: Output
            }], fileSelected: [{
                type: Output
            }], fileInput: [{
                type: ViewChild,
                args: ['fileInput', { static: false }]
            }] } });

class MultipleFileUploadComponent {
    constructor(fb, cd) {
        this.fb = fb;
        this.cd = cd;
        this.hasInitFormWithValues = false;
        this.onNewFileUploaded = new Subject();
    }
    ngOnInit() {
        this.filesForm = this.createForm();
        // Add existing images
        const images = this.form.get(this.controlName).value;
        if (images) {
            images.forEach((file) => { this.add(file); });
            this.hasInitFormWithValues = true;
        }
        else {
            const subscription = this.form.get(this.controlName).valueChanges.subscribe((newFiles) => {
                if (!this.hasInitFormWithValues) {
                    newFiles.forEach((file) => { this.add(file); });
                    this.hasInitFormWithValues = true;
                }
                subscription.unsubscribe();
            });
        }
    }
    get filesArray() {
        return this.filesForm.get('files');
    }
    triggerFileSelection() {
        this.fileInput.nativeElement.click();
    }
    onFileSelected(event) {
        const inputElement = event.target;
        const files = inputElement.files;
        if (!files || !files.length) {
            return;
        }
        const file = files[0];
        this.hasInitFormWithValues = true;
        const form = this.fb.group({
            file: [file]
        });
        this.filesForm.get('files').push(form);
        this.cd.detectChanges();
    }
    onUploadChange(file) {
        this.onNewFileUploaded.next(file);
        if (file && file.inProgress) {
            this.add();
        }
        this.cd.detectChanges();
    }
    remove(index) {
        // Get the specific SingleFileUploadComponent instance by index
        const fileUploadComponent = this.fileUploadComponents.toArray()[index];
        if (fileUploadComponent) {
            fileUploadComponent.resetUpload();
        }
        this.filesForm.get('files').removeAt(index);
        this.cd.detectChanges();
    }
    add(file) {
        if (!file) {
            return;
        }
        const form = this.fb.group({
            file: [file]
        });
        this.filesForm.get('files').push(form);
    }
    createForm() {
        return this.fb.group({
            files: this.fb.array([])
        });
    }
}
MultipleFileUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MultipleFileUploadComponent, deps: [{ token: i1$1.FormBuilder }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
MultipleFileUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: MultipleFileUploadComponent, selector: "app-multiple-file-upload", inputs: { form: "form", controlName: "controlName", storageBucket: "storageBucket" }, outputs: { onNewFileUploaded: "onNewFileUploaded" }, viewQueries: [{ propertyName: "fileInput", first: true, predicate: ["fileInput"], descendants: true }, { propertyName: "fileUploadComponents", predicate: SingleFileUploadComponent, descendants: true }], ngImport: i0, template: "<ng-content></ng-content>\r\n\r\n<ng-container *ngFor=\"let fileFormGroup of filesArray.controls; let i = index\">\r\n    <!-- File upload component for each file -->\r\n    <app-single-file-upload \r\n        id=\"{{i}}\"\r\n        style=\"display: none;\"\r\n        [form]=\"fileFormGroup\" \r\n        controlName=\"file\" \r\n        [storageBucket]=\"storageBucket\"\r\n        (onRemove)=\"remove(i)\"\r\n        (onChange)=\"onUploadChange($event)\">\r\n    </app-single-file-upload>\r\n\r\n</ng-container>\r\n\r\n<!-- Hidden file input for triggering file selection -->\r\n<input type=\"file\" #fileInput hidden aria-label=\"Input Selector\" name=\"file-upload-input\"\r\n    (change)=\"onFileSelected($event)\" />", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: SingleFileUploadComponent, selector: "app-single-file-upload", inputs: ["form", "controlName", "uploadErrorMessage", "storageBucket", "customTemplate", "autoUpload", "externalFile"], outputs: ["progressEmitter", "onRemove", "onChange", "onNewCoffeeFileUploaded", "fileSelected"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MultipleFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-multiple-file-upload', template: "<ng-content></ng-content>\r\n\r\n<ng-container *ngFor=\"let fileFormGroup of filesArray.controls; let i = index\">\r\n    <!-- File upload component for each file -->\r\n    <app-single-file-upload \r\n        id=\"{{i}}\"\r\n        style=\"display: none;\"\r\n        [form]=\"fileFormGroup\" \r\n        controlName=\"file\" \r\n        [storageBucket]=\"storageBucket\"\r\n        (onRemove)=\"remove(i)\"\r\n        (onChange)=\"onUploadChange($event)\">\r\n    </app-single-file-upload>\r\n\r\n</ng-container>\r\n\r\n<!-- Hidden file input for triggering file selection -->\r\n<input type=\"file\" #fileInput hidden aria-label=\"Input Selector\" name=\"file-upload-input\"\r\n    (change)=\"onFileSelected($event)\" />" }]
        }], ctorParameters: function () { return [{ type: i1$1.FormBuilder }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { form: [{
                type: Input
            }], controlName: [{
                type: Input
            }], storageBucket: [{
                type: Input
            }], onNewFileUploaded: [{
                type: Output
            }], fileUploadComponents: [{
                type: ViewChildren,
                args: [SingleFileUploadComponent]
            }], fileInput: [{
                type: ViewChild,
                args: ['fileInput', { static: false }]
            }] } });

class CoffeeFileUploadContext {
    constructor() {
        this.optimizationInfo = '';
        this.progress = 0;
        this.preview = null;
        this.isImage = () => false;
        this.errorMessage = '';
    }
}

class CoffeeFileUploadComponent {
    constructor() {
        this.hasInit = false;
        this.type = "default";
        this.storageBucket = 'default';
        this.autoUpload = true;
        this.onNewFileUploaded = new Subject();
        this.hasFilesInProgress = new EventEmitter();
        this.fileSelected = new EventEmitter();
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.hasInit = true;
        }, 0);
    }
    ngOnInit() { }
    get file() {
        if (!this.fileUploadComponents || !this.fileUploadComponents.first || !this.hasInit) {
            return new CoffeeFileUploadContext();
        }
        return this.fileUploadComponents.first.context;
    }
    get files() {
        if (!this.multipleFileUploadComponents || !this.multipleFileUploadComponents.first ||
            !this.multipleFileUploadComponents.first.fileUploadComponents ||
            !this.multipleFileUploadComponents.first.fileUploadComponents.length
            || !this.hasInit) {
            return [new CoffeeFileUploadContext()];
        }
        return this.multipleFileUploadComponents.first.fileUploadComponents.map(component => component.context);
    }
    click() {
        if (this.type == 'multiple') {
            if (!this.multipleFileUploadComponents || !this.multipleFileUploadComponents.first) {
                return;
            }
            return this.multipleFileUploadComponents.first.triggerFileSelection();
        }
        if (!this.fileUploadComponents || !this.fileUploadComponents.first) {
            return;
        }
        return this.fileUploadComponents.first.context.click();
    }
    onFileUploadChange(file) {
        if (file && file.inProgress) {
            this.hasFilesInProgress.next(true);
            return;
        }
        else if (file) {
            this.onNewFileUploaded.next(file);
        }
        if (this.type == 'multiple') {
            this.hasFilesInProgress.next(this.files.some(file => file.progress > 0 && file.progress < 100));
        }
        else {
            this.hasFilesInProgress.next(this.file.progress > 0 && this.file.progress < 100);
        }
    }
    handleFileSelected(file) {
        this.fileSelected.emit(file);
    }
}
CoffeeFileUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeFileUploadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CoffeeFileUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: CoffeeFileUploadComponent, selector: "ngx-coffee-file-upload", inputs: { type: "type", form: "form", controlName: "controlName", storageBucket: "storageBucket", autoUpload: "autoUpload", fileToUpload: "fileToUpload" }, outputs: { onNewFileUploaded: "onNewFileUploaded", hasFilesInProgress: "hasFilesInProgress", fileSelected: "fileSelected" }, viewQueries: [{ propertyName: "fileUploadComponents", predicate: SingleFileUploadComponent, descendants: true }, { propertyName: "multipleFileUploadComponents", predicate: MultipleFileUploadComponent, descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"type == 'default'\">\r\n    <app-single-file-upload\r\n        [form]=\"form\"\r\n        [controlName]=\"controlName\"\r\n        [storageBucket]=\"storageBucket\"\r\n        [autoUpload]=\"autoUpload\"\r\n        [externalFile]=\"fileToUpload\"\r\n        (fileSelected)=\"handleFileSelected($event)\"\r\n        (onChange)=\"onFileUploadChange($event)\">\r\n        <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n    </app-single-file-upload>\r\n</ng-container>\r\n\r\n<ng-container *ngIf=\"type == 'multiple'\">\r\n    <app-multiple-file-upload\r\n        [form]=\"form\"\r\n        [controlName]=\"controlName\"\r\n        [storageBucket]=\"storageBucket\"\r\n        (onNewFileUploaded)=\"onFileUploadChange($event)\">\r\n        <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n    </app-multiple-file-upload>\r\n</ng-container>\r\n\r\n<ng-template #contentTpl><ng-content></ng-content></ng-template>", dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: MultipleFileUploadComponent, selector: "app-multiple-file-upload", inputs: ["form", "controlName", "storageBucket"], outputs: ["onNewFileUploaded"] }, { kind: "component", type: SingleFileUploadComponent, selector: "app-single-file-upload", inputs: ["form", "controlName", "uploadErrorMessage", "storageBucket", "customTemplate", "autoUpload", "externalFile"], outputs: ["progressEmitter", "onRemove", "onChange", "onNewCoffeeFileUploaded", "fileSelected"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-coffee-file-upload', template: "<ng-container *ngIf=\"type == 'default'\">\r\n    <app-single-file-upload\r\n        [form]=\"form\"\r\n        [controlName]=\"controlName\"\r\n        [storageBucket]=\"storageBucket\"\r\n        [autoUpload]=\"autoUpload\"\r\n        [externalFile]=\"fileToUpload\"\r\n        (fileSelected)=\"handleFileSelected($event)\"\r\n        (onChange)=\"onFileUploadChange($event)\">\r\n        <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n    </app-single-file-upload>\r\n</ng-container>\r\n\r\n<ng-container *ngIf=\"type == 'multiple'\">\r\n    <app-multiple-file-upload\r\n        [form]=\"form\"\r\n        [controlName]=\"controlName\"\r\n        [storageBucket]=\"storageBucket\"\r\n        (onNewFileUploaded)=\"onFileUploadChange($event)\">\r\n        <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n    </app-multiple-file-upload>\r\n</ng-container>\r\n\r\n<ng-template #contentTpl><ng-content></ng-content></ng-template>" }]
        }], ctorParameters: function () { return []; }, propDecorators: { type: [{
                type: Input
            }], form: [{
                type: Input
            }], controlName: [{
                type: Input
            }], storageBucket: [{
                type: Input
            }], autoUpload: [{
                type: Input
            }], fileToUpload: [{
                type: Input
            }], onNewFileUploaded: [{
                type: Output
            }], hasFilesInProgress: [{
                type: Output
            }], fileSelected: [{
                type: Output
            }], fileUploadComponents: [{
                type: ViewChildren,
                args: [SingleFileUploadComponent]
            }], multipleFileUploadComponents: [{
                type: ViewChildren,
                args: [MultipleFileUploadComponent]
            }] } });

class CoffeeFileUploadModule {
}
CoffeeFileUploadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeFileUploadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CoffeeFileUploadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CoffeeFileUploadModule, declarations: [CoffeeFileUploadComponent,
        MultipleFileUploadComponent,
        SingleFileUploadComponent], imports: [CommonModule], exports: [CoffeeFileUploadComponent] });
CoffeeFileUploadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeFileUploadModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CoffeeFileUploadModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CoffeeFileUploadComponent,
                        MultipleFileUploadComponent,
                        SingleFileUploadComponent
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        CoffeeFileUploadComponent,
                    ]
                }]
        }] });

/*
 * Public API Surface of coffee
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CoffeeAuthSocial, CoffeeFileUpload, CoffeeFileUploadComponent, CoffeeFileUploadContext, CoffeeFileUploadModule, CoffeeModel, CoffeeModule, CoffeeService, CoffeeSocialGoogleButtonComponent, CoffeeSocialGoogleButtonModule, FilterResponse, Pager, initializeMsal, sortBy, where, whereContains, whereDate, whereIn, whereIs, whereNotIn, whereOr, withQueryParameter };
